import React, { useEffect, useState } from 'react';
import { Button } from '@material-ui/core';
import Pagination from '../pagination/Pagination';
import {
  monthNames,
  formatCurrencyAbbreviated,
  generateCSV,
} from '../../utils/utils';
import type {
  UsageHistoryRecord,
  UsageHistorySectionProps,
} from '../../interfaces/types';

const SkeletonLoader: React.FC = () => {
  return (
    <div className="cost-center-table-shell">
      <table className="cost-center-table">
        <thead>
          <tr>
            <th>Period</th>
            <th>State</th>
            <th>Number of users</th>
            <th>Budget amount</th>
            <th>Consumed amount</th>
          </tr>
        </thead>
        <tbody>
          {[...Array(2)].map((_, idx) => (
            <tr key={idx}>
              <td>
                <div
                  className="skeleton-loader"
                  style={{ width: '80px', height: '16px' }}
                />
              </td>
              <td>
                <div
                  className="skeleton-loader"
                  style={{
                    width: '100px',
                    height: '28px',
                    borderRadius: '14px',
                  }}
                />
              </td>
              <td>
                <div
                  className="skeleton-loader"
                  style={{ width: '40px', height: '16px' }}
                />
              </td>
              <td>
                <div
                  className="skeleton-loader"
                  style={{ width: '70px', height: '16px' }}
                />
              </td>
              <td>
                <div
                  className="skeleton-loader"
                  style={{ width: '70px', height: '16px' }}
                />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export const UsageHistorySection: React.FC<UsageHistorySectionProps> = ({
  selectedCostCenterDataLength,
  usageHistoryPage,
  onPageChange,
  // onExportCSV,
  monthAndYearWiseCostCenters,
  isLoading = false, // Default to false if not provided
}) => {
  const [usageHistoryRowsPerPage, setUsageHistoryRowsPerPage] = useState(12);

  const handleUsageHistoryRowsPerPageChange = (newRowsPerPage: number) => {
    setUsageHistoryRowsPerPage(newRowsPerPage);
    onPageChange(0);
  };
  const currentYear = new Date().getFullYear();
  const [allRecords, setAllRecords] = React.useState<UsageHistoryRecord[]>([]);

  useEffect(() => {
    if (
      monthAndYearWiseCostCenters &&
      monthAndYearWiseCostCenters[currentYear]
    ) {
      const records: UsageHistoryRecord[] = [];
      const months = Object.keys(monthAndYearWiseCostCenters[currentYear]).sort(
        (a, b) => parseInt(b) - parseInt(a), // Sort months in descending order
      );
      months.forEach(month => {
        const data = monthAndYearWiseCostCenters[currentYear][month];
        const totalData = {
          users: 0,
          budget: 0,
          consumed: 0,
        };
        data.forEach((record: any) => {
          totalData.users += record.resources.length;
          totalData.budget += record.budgetAmount || 0;
          totalData.consumed += record.consumedAmount || 0;
        });
        records.push({
          month: monthNames[parseInt(month) - 1] + ' ' + currentYear,
          state: 'active',
          users: totalData.users,
          budget: formatCurrencyAbbreviated(totalData.budget),
          consumed: formatCurrencyAbbreviated(totalData.consumed),
        });
      });
      setAllRecords(records);
    }
  }, [monthAndYearWiseCostCenters, currentYear]);
  // const allRecords: UsageHistoryRecord[] = [
  //   {
  //     month: 'Jul 2026',
  //     state: 'Active',
  //     users: 40,
  //     budget: '$18,720',
  //     consumed: '$13,150',
  //   },
  //   {
  //     month: 'Jun 2026',
  //     state: 'Active',
  //     users: 41,
  //     budget: '$18,180',
  //     consumed: '$12,770',
  //   },
  //   {
  //     month: 'May 2026',
  //     state: 'Active',
  //     users: 39,
  //     budget: '$17,460',
  //     consumed: '$12,260',
  //   },
  // ];

  const paginatedRecords = allRecords.slice(
    usageHistoryPage * usageHistoryRowsPerPage,
    usageHistoryPage * usageHistoryRowsPerPage + usageHistoryRowsPerPage,
  );

  const handleExportCSV = () => {
    if (!allRecords || allRecords.length === 0) return;

    const headers = [
      'Period',
      'State',
      'Number of Users',
      'Budget Amount',
      'Consumed Amount',
    ];

    const rows = allRecords.map(record => [
      record.month,
      record.state,
      record.users.toString(),
      record.budget,
      record.consumed,
    ]);

    const filename = `usage-history-${currentYear}.csv`;
    generateCSV(headers, rows, filename);
  };

  return (
    <section className="cost-center-detail-section cost-center-detail-section--usage-history">
      <div className="cost-center-section-heading">
        <div>
          <h2>Usage History</h2>
          <p>Monthly spend history for the selected cost center.</p>
        </div>
        <div className="cost-center-section-heading__actions">
          <span className="cost-center-record-count">
            {selectedCostCenterDataLength} records
          </span>
          <select
            className="cost-center-usage-history-year-filter"
            aria-label="Filter usage history by year"
            defaultValue="2026"
          >
            <option value="all">All Years</option>
            <option value="2026">2026</option>
          </select>
          <Button
            variant="contained"
            color="primary"
            size="small"
            onClick={handleExportCSV}
            style={{ marginLeft: '8px' }}
            disabled={isLoading}
          >
            Export CSV
          </Button>
        </div>
      </div>

      {isLoading ? (
        <SkeletonLoader />
      ) : (
        <div className="cost-center-table-shell">
          <table className="cost-center-table">
            <thead>
              <tr>
                <th>Period</th>
                <th>State</th>
                <th>Number of users</th>
                <th>Budget amount</th>
                <th>Consumed amount</th>
              </tr>
            </thead>
            <tbody>
              {paginatedRecords.map((record, idx) => (
                <tr key={idx}>
                  <td>{record.month}</td>
                  <td>
                    <span className="cost-center-pill cost-center-pill--status cost-center-status-pill">
                      {record.state}
                    </span>
                  </td>
                  <td>{record.users}</td>
                  <td>{record.budget}</td>
                  <td className="cost-center-consumed-value">
                    {record.consumed}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Pagination
        page={usageHistoryPage}
        rowsPerPage={usageHistoryRowsPerPage}
        rowsPerPageOptions={[3, 6, 9, 12]}
        count={allRecords.length}
        onPageChange={onPageChange}
        onRowsPerPageChange={handleUsageHistoryRowsPerPageChange}
      />
    </section>
  );
};
